
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { ActionType, DatepickerComponent, FormControlFeedbackComponent, IFormFieldsValidation, Loader, LoaderSpinnerComponent, UnsubscriberClass, ValidationServiceHelper, isBeforeDay } from '@aston/foundation';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ctl } from '../../helpers';
import { ISubsidiaryFormUserInactivity, ISubsidiaryUser } from '../../models';

@Component({
    selector: 'middle-users-inactivity-form.component',
    imports: [AsyncPipe, TranslateModule, NgbTooltipModule, FormsModule, ReactiveFormsModule, LoaderSpinnerComponent, FormControlFeedbackComponent, DatepickerComponent],
    templateUrl: './middle-users-inactivity-form.component.html'
})
export class MiddleUsersInactivityFormModalComponent extends UnsubscriberClass implements OnInit {
	@Input({ required: true }) mode!: ActionType;
	@Input({ required: true }) user!: ISubsidiaryUser;
	@Input({ required: true }) validationHelperGetter!: Observable<[ValidationServiceHelper, IFormFieldsValidation, string]>;
	@Input({ required: true }) updateUserInactivity!: (form: ISubsidiaryFormUserInactivity) => void;
	@Input({ required: true }) loader = Loader.empty();

	@Output() submitForm: EventEmitter<void> = new EventEmitter();
	@Output() cancel: EventEmitter<void> = new EventEmitter();

	private fb = inject(UntypedFormBuilder);
	public activeModal = inject(NgbActiveModal);

	today = new Date();
	form = this.fb.group({
		start: new UntypedFormControl(),
		end: new UntypedFormControl(),
		setActive: new UntypedFormControl(),
	});

	ActionType = ActionType;
	validationHelper!: ValidationServiceHelper;
	rulesetFields!: IFormFieldsValidation;

	get startField() {
		return ctl(this.form?.get('start'))
	}

	get endField() {
		return ctl(this.form?.get('end'))
	}

	get setActiveField() {
		return ctl(this.form?.get('setActive'))
	}

    ngOnInit() {
		this.validationHelperGetter.pipe(
			tap(([helper, fields]) => {
				this.validationHelper = helper;
				this.rulesetFields = fields;

				this.setFormValidators();

				this.form.patchValue({
					start: this.user.inactivityBeginDate || this.today,
					end: this.user.inactivityEndDate,
					setActive: false,
				})

				if (this.user.inactivityBeginDate) {
					this.startField.markAsDirty();
					this.startField.updateValueAndValidity();
				}

				this.form.valueChanges.pipe(
					takeUntil(this.destroySubscriptions$)
				).subscribe(_ => {
					this.startField.updateValueAndValidity({ onlySelf: true });
					this.endField.updateValueAndValidity({ onlySelf: true });
				});
			}),
			takeUntil(this.destroySubscriptions$)
		).subscribe();
	}

	setFormValidators() {
		this.validationHelper.control(this.startField, this.rulesetFields.Begin);

		// because AfterFactorNowValidator allow nulls
		this.startField.addValidators(Validators.required)
		this.endField.addValidators(this.dateIsEqualOrAfter(this.startField))
	}

	dateIsEqualOrAfter(other: AbstractControl): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value || !other.value) return null;
			return isBeforeDay(control.value, other.value) ? { rangeInconsistency: true } : null;
		}
	}

	reactivate(target: EventTarget) {
		if ((<HTMLInputElement>target).checked) {
			this.startField.disable();
			this.endField.disable();

		} else {
			this.startField.enable();
			this.endField.enable();
		}
	}

	onSubmit() {
		this.updateUserInactivity({
			...this.form.value,
			mode: this.mode,
			begin: this.startField.value || null,
			end: this.endField.value || null,
			forUserId: this.user.userId,
			reactivateUser: !!this.setActiveField.value
		});
	}
}
